import { useState, useEffect, useMemo } from 'react';
import { useCustomItemPicker } from '../../hooks/useCustomItemPicker';
import { useLocation, useNavigate } from 'react-router-dom';
import OrderForm from '../../components/orders/OrderForm';
import Alert from '../../components/common/Alert';
import Loading from '../../components/common/Loading';
import api from '../../services/api';
import { getAddresses } from '../../services/customers';
import { getSessions } from '../../services/sessions';
import { getProducts } from '../../services/products';
import { createOrderAsEmployee } from '../../services/orders';
import { formatCurrency } from '../../utils/formatters';
import { SAMPLE_ELIGIBLE_UNITS } from '../../utils/constants';
import './NewOrder.css';

/**
 * Employee new order page — place an order on behalf of a customer.
 *
 * Expects location.state = { customerId, companyName } set when navigating
 * from the customer detail view.
 */
const EmployeeNewOrder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { customerId, companyName } = location.state || {};

  // ── Product picker state ───────────────────────────────────
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [pickQuantity, setPickQuantity] = useState(1);
  const [pickIsSample, setPickIsSample] = useState(false);

  // ── Custom item picker ─────────────────────────────────────
  const {
    customDesc, setCustomDesc,
    customQty, setCustomQty,
    customPrice, setCustomPrice,
    handleAdd: handleAddCustomItem,
    isValid: isCustomValid,
  } = useCustomItemPicker();

  // ── Order items (own cart, no CartContext) ─────────────────
  const [items, setItems] = useState([]);
  const [shippingCharge, setShippingCharge] = useState(0);

  // ── Addresses + sessions ───────────────────────────────────
  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);

  // ── Customer contact ───────────────────────────────────────
  const [companyUsers, setCompanyUsers] = useState([]);
  const [companyUsersLoading, setCompanyUsersLoading] = useState(true);
  const [customerUserId, setCustomerUserId] = useState(null);

  // ── Submission ─────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // ── Guard: must arrive with a customer ────────────────────
  useEffect(() => {
    if (!customerId) {
      navigate('/employee/customers', { replace: true });
    }
  }, [customerId, navigate]);

  // ── Fetch products ─────────────────────────────────────────
  useEffect(() => {
    setProductsLoading(true);
    getProducts()
      .then((data) => {
        const list = Array.isArray(data) ? data : (data.results ?? []);
        setProducts(list);
      })
      .catch(() => setProducts([]))
      .finally(() => setProductsLoading(false));
  }, []);

  // ── Fetch company users ────────────────────────────────────
  useEffect(() => {
    if (!customerId) return;
    setCompanyUsersLoading(true);
    api.get(`companies/${customerId}/users/`)
      .then((res) => {
        const users = res.data.results ?? res.data.users ?? [];
        setCompanyUsers(users);
        if (users.length === 1) setCustomerUserId(users[0].user.id);
      })
      .catch(() => setCompanyUsers([]))
      .finally(() => setCompanyUsersLoading(false));
  }, [customerId]);

  // ── Fetch customer addresses ───────────────────────────────
  useEffect(() => {
    if (!customerId) return;
    setAddressesLoading(true);
    getAddresses(customerId)
      .then((data) => {
        const all = Array.isArray(data) ? data : (data.results ?? []);
        setAddresses(all.filter((a) => a.address_type === 'shipping'));
      })
      .catch(() => setAddresses([]))
      .finally(() => setAddressesLoading(false));
  }, [customerId]);

  // ── Fetch upcoming sessions ────────────────────────────────
  useEffect(() => {
    setSessionsLoading(true);
    getSessions({ employee: true })
      .then((data) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        setSessions(data.filter((s) => {
          const [y, m, d] = s.mill_end.split('-').map(Number);
          return new Date(y, m - 1, d) >= today;
        }));
      })
      .catch(() => setSessions([]))
      .finally(() => setSessionsLoading(false));
  }, []);

  // ── Derived: selected product's variants ──────────────────
  const selectedProduct = products.find((p) => String(p.id) === String(selectedProductId));

  // Sort variants by the leading number in their name (e.g. "2 lb bag" < "10 lb bag")
  const variants = useMemo(
    () =>
      [...(selectedProduct?.variants ?? [])]
        .filter((v) => !pickIsSample || SAMPLE_ELIGIBLE_UNITS.has(v.unit ?? v.variant_name))
        .sort((a, b) => (parseFloat(a.variant_name) || 0) - (parseFloat(b.variant_name) || 0)),
    [selectedProduct, pickIsSample]
  );
  const selectedVariant = variants.find((v) => String(v.id) === String(selectedVariantId));

  const handleProductChange = (e) => {
    setSelectedProductId(e.target.value);
    setSelectedVariantId('');
    setPickQuantity(1);
  };

  const handleVariantChange = (e) => {
    setSelectedVariantId(e.target.value);
    setPickQuantity(1);
  };

  // ── Add item to order ──────────────────────────────────────
  const handleAddItem = () => {
    if (!selectedVariant) return;

    setItems((prev) => {
      const existing = prev.find(
        (i) => i.productVariantId === selectedVariant.id && i.is_sample === pickIsSample
      );
      if (existing) {
        return prev.map((i) =>
          i.productVariantId === selectedVariant.id && i.is_sample === pickIsSample
            ? { ...i, quantity: i.quantity + pickQuantity }
            : i
        );
      }
      return [
        ...prev,
        {
          id: `${selectedVariant.id}-${pickIsSample ? 'sample' : 'reg'}`,
          productVariantId: selectedVariant.id,
          quantity: pickQuantity,
          is_sample: pickIsSample,
          product: { id: selectedProduct.id, name: selectedProduct.name },
          variant: {
            id: selectedVariant.id,
            variant_name: selectedVariant.variant_name,
            sku: selectedVariant.sku,
          },
          unit_price: pickIsSample ? 0 : (selectedVariant.price ?? selectedVariant.price_dollars ?? 0),
        },
      ];
    });

    // Reset picker
    setSelectedProductId('');
    setSelectedVariantId('');
    setPickQuantity(1);
  };

  const handleUpdateItem = (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    setItems((prev) =>
      prev.map((i) => (i.id === itemId ? { ...i, quantity: newQuantity } : i))
    );
  };

  const handleUpdateItemPrice = (itemId, value) => {
    const parsed = value === '' ? '' : parseFloat(value);
    setItems((prev) =>
      prev.map((i) => (i.id === itemId ? { ...i, unit_price: parsed } : i))
    );
  };

  const handleUpdateShipping = (value) => {
    setShippingCharge(value === '' ? '' : parseFloat(value));
  };

  const handleRemoveItem = (itemId) => {
    setItems((prev) => prev.filter((i) => i.id !== itemId));
  };


  // ── Submit ─────────────────────────────────────────────────
  const handleSubmit = async (formData) => {
    try {
      setSubmitError(null);
      setIsSubmitting(true);

      const isPickup = formData.fulfillment_type === 'pickup';
      const orderData = {
        company: customerId,
        fulfillment_type: formData.fulfillment_type,
        ...(formData.session_id != null ? { session: formData.session_id } : {}),
        special_instructions: formData.special_instructions || '',
        ...(!isPickup && formData.delivery_address_id
          ? { delivery_address: formData.delivery_address_id }
          : {}),
        ...(customerUserId != null ? { customer_user: customerUserId } : {}),
        ...(shippingCharge !== '' && shippingCharge > 0
          ? { shipping_charge_cents: Math.round(parseFloat(shippingCharge) * 100) }
          : {}),
        items: items.map((item) => {
          if (item.isCustom) {
            return {
              description: item.description,
              quantity: item.quantity,
              unit_price_cents: Math.round(parseFloat(item.unit_price) * 100),
            };
          }
          return {
            product_variant_id: item.productVariantId,
            quantity: item.quantity,
            is_sample: item.is_sample || false,
            ...(!item.is_sample && item.unit_price != null && item.unit_price !== ''
              ? { unit_price_cents: Math.round(parseFloat(item.unit_price) * 100) }
              : {}),
          };
        }),
      };

      const created = await createOrderAsEmployee(orderData);
      navigate(`/employee/orders/${created.id}`, {
        state: { successMessage: `Order placed for ${companyName}.` },
      });
    } catch (error) {
      setSubmitError(error.message || 'Failed to place order. Please try again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/employee/customers');
  };

  if (!customerId) return null;

  return (
    <div className="employee-page">
      <div className="employee-new-order">
        {/* ── Header ─────────────────────────────────────────── */}
        <div className="employee-page__header">
          <p className="employee-page__eyebrow">Employee Portal / Customers / {companyName}</p>
          <h1 className="employee-page__title">New Order</h1>
        </div>

        {/* ── Error ──────────────────────────────────────────── */}
        {submitError && (
          <div className="employee-new-order__alert">
            <Alert type="error">{submitError}</Alert>
          </div>
        )}

        {/* ── Product picker ─────────────────────────────────── */}
        <div className="employee-new-order__picker-wrap">
          <div className="employee-new-order__picker-label">Add Products</div>
          {productsLoading ? (
            <Loading text="Loading products…" />
          ) : (
            <div className="employee-new-order__picker">
              <label className="employee-new-order__sample-label">
                <input
                  type="checkbox"
                  className="employee-new-order__sample-checkbox"
                  checked={pickIsSample}
                  onChange={(e) => {
                    setPickIsSample(e.target.checked);
                    setSelectedProductId('');
                    setSelectedVariantId('');
                  }}
                />
                Sample
              </label>

              <select
                className="employee-new-order__select"
                value={selectedProductId}
                onChange={handleProductChange}
                aria-label="Select product"
              >
                <option value="">— Select product —</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>

              <select
                className="employee-new-order__select"
                value={selectedVariantId}
                onChange={handleVariantChange}
                disabled={!selectedProduct}
                aria-label="Select variant"
              >
                <option value="">— Select size —</option>
                {variants.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.variant_name}
                    {pickIsSample
                      ? ' — Free'
                      : (v.price ?? v.price_dollars) != null
                        ? ` — ${formatCurrency(v.price ?? v.price_dollars)}`
                        : ''}
                  </option>
                ))}
              </select>

              <div className="employee-new-order__qty-wrap">
                <button
                  type="button"
                  className="employee-new-order__qty-btn"
                  onClick={() => setPickQuantity((q) => Math.max(1, q - 1))}
                  disabled={pickQuantity <= 1}
                  aria-label="Decrease quantity"
                >−</button>
                <span className="employee-new-order__qty-val">{pickQuantity}</span>
                <button
                  type="button"
                  className="employee-new-order__qty-btn"
                  onClick={() => setPickQuantity((q) => q + 1)}
                  aria-label="Increase quantity"
                >+</button>
              </div>

              <button
                type="button"
                className="employee-new-order__add-btn"
                onClick={handleAddItem}
                disabled={!selectedVariant}
              >
                Add
              </button>
            </div>
          )}
        </div>

        {/* ── Custom item ────────────────────────────────────── */}
        <div className="employee-new-order__picker-wrap">
          <div className="employee-new-order__picker-label">Add Custom Item</div>
          <div className="employee-new-order__picker">
            <input
              type="text"
              className="employee-new-order__custom-desc"
              placeholder="Description (e.g. Whole grain Redeemer, 50 lbs)"
              value={customDesc}
              onChange={(e) => setCustomDesc(e.target.value)}
              maxLength={255}
              aria-label="Custom item description"
            />

            <div className="employee-new-order__qty-wrap">
              <button
                type="button"
                className="employee-new-order__qty-btn"
                onClick={() => setCustomQty((q) => Math.max(1, q - 1))}
                disabled={customQty <= 1}
                aria-label="Decrease quantity"
              >−</button>
              <span className="employee-new-order__qty-val">{customQty}</span>
              <button
                type="button"
                className="employee-new-order__qty-btn"
                onClick={() => setCustomQty((q) => q + 1)}
                aria-label="Increase quantity"
              >+</button>
            </div>

            <div className="employee-new-order__price-override">
              <span className="employee-new-order__price-prefix">$</span>
              <input
                type="number"
                className="employee-new-order__price-input"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={customPrice}
                onChange={(e) => setCustomPrice(e.target.value)}
                aria-label="Custom item price"
              />
            </div>

            <button
              type="button"
              className="employee-new-order__add-btn"
              onClick={() => handleAddCustomItem((item) => setItems((prev) => [...prev, item]))}
              disabled={!isCustomValid}
            >
              Add
            </button>
          </div>
        </div>

        {/* ── Customer contact ───────────────────────────────── */}
        <div className="employee-new-order__customer-contact">
          <label className="employee-new-order__contact-label">
            Customer Contact
          </label>
          {companyUsersLoading ? (
            <p className="employee-new-order__contact-hint">Loading contacts…</p>
          ) : companyUsers.length === 0 ? (
            <p className="employee-new-order__contact-hint">No customer contacts found.</p>
          ) : (
            <select
              className="employee-new-order__select"
              value={customerUserId ?? ''}
              onChange={(e) => setCustomerUserId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">— Select contact —</option>
              {companyUsers.map((profile) => (
                <option key={profile.user.id} value={profile.user.id}>
                  {profile.user.first_name} {profile.user.last_name} ({profile.user.email})
                </option>
              ))}
            </select>
          )}
          <p className="employee-new-order__contact-note">
            Order confirmation will be sent to this contact.
          </p>
        </div>

        {/* ── Order form ─────────────────────────────────────── */}
        <div className="employee-new-order__form">
          <OrderForm
            items={items}
            addresses={addresses}
            addressesLoading={addressesLoading}
            sessions={sessions}
            sessionsLoading={sessionsLoading}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            cancelLabel="← Back to Customers"
            onUpdateItem={handleUpdateItem}
            onRemoveItem={handleRemoveItem}
            onUpdatePrice={handleUpdateItemPrice}
            onUpdateShipping={handleUpdateShipping}
            shippingChargeDollars={shippingCharge}
            isEmployee={true}
            isSubmitting={isSubmitting}
          />
        </div>
      </div>
    </div>
  );
};

export default EmployeeNewOrder;
